import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleExclamation,
  faLocationDot,
} from "@fortawesome/free-solid-svg-icons";
import MapModel from "./MapModel";
import {
  countryDescription,
  MAX_DESCRIPTION_LENGTH,
} from "../../utils/countries";

const emptyCountry = {
  name_ar: "",
  name_en: "",
  city_ar: "",
  city_en: "",
  lat: "",
  lng: "",
  hq: false,
};

const inputClass =
  "w-full h-[30px] font-[400] text-[15px] rounded border-[#c4c8cf] placeholder:text-[12px] placeholder:font-[400]";

// Add or edit one country of the "Global presence" map.
function NewCountry({ country, setShowModal, onSave }) {
  const [values, setValues] = useState({ ...emptyCountry, ...country });
  const [showMap, setShowMap] = useState(false);
  const [error, setError] = useState(null);

  const changeHandler = (e) => {
    setValues({ ...values, [e.target.name]: e.target.value });
  };

  const submit = (e) => {
    e.preventDefault();
    const lat = Number(values.lat);
    const lng = Number(values.lng);
    if (!values.name_ar.trim() || !values.name_en.trim()) {
      setError("Please enter the country name in Arabic and English");
    } else if (
      values.lat === "" ||
      values.lng === "" ||
      !Number.isFinite(lat) ||
      !Number.isFinite(lng) ||
      Math.abs(lat) > 90 ||
      Math.abs(lng) > 180
    ) {
      setError("Please pick the location on the map");
    } else if (countryDescription(values).length > MAX_DESCRIPTION_LENGTH) {
      setError("The names are too long, please shorten them");
    } else {
      onSave({ ...values, lat, lng });
      setShowModal(false);
    }
  };

  const field = (name, label, placeholder, dir, required) => (
    <div className="w-[48%] mt-4">
      <label className="text-[14px] font-[400]" htmlFor={`country-${name}`}>
        {label}
        {required && <span className="text-[red] font-[700]"> *</span>}
      </label>
      <input
        id={`country-${name}`}
        name={name}
        dir={dir}
        className={inputClass}
        placeholder={placeholder}
        value={values[name]}
        onChange={changeHandler}
        type="text"
      />
    </div>
  );

  const hasLocation = values.lat !== "" && values.lng !== "";

  return (
    <>
      <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 outline-none focus:outline-none">
        <div className="relative my-6 w-[85%] max-w-[900px]">
          <form onSubmit={submit}>
            <div className="border-0 rounded-xl shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
              <div className="flex justify-between h-[50px] rounded-t">
                <div className="text-[18px] w-[30%] text-center m-auto font-semibold">
                  {country ? "Edit Country" : "Add New Country"}
                </div>
                <div className="w-[70%] rounded-bl-xl rounded-tr-xl bg-[#E9F6FD] h-full"></div>
              </div>
              <div className="relative p-6 w-full">
                <div className="flex flex-wrap justify-between">
                  {field("name_ar", "Country name - AR", "مثال: السعودية", "rtl", true)}
                  {field("name_en", "Country name - EN", "e.g. Saudi Arabia", "ltr", true)}
                  {field("city_ar", "City - AR", "مثال: الرياض", "rtl")}
                  {field("city_en", "City - EN", "e.g. Riyadh", "ltr")}
                  <div className="w-[48%] mt-4">
                    <label className="text-[14px] font-[400]">
                      Location on map
                      <span className="text-[red] font-[700]"> *</span>
                    </label>
                    <div className="flex">
                      <input
                        readOnly
                        className={inputClass}
                        placeholder="Click the pin to pick the location"
                        value={
                          hasLocation ? `${values.lat}, ${values.lng}` : ""
                        }
                        onClick={() => setShowMap(true)}
                      />
                      <div
                        onClick={() => setShowMap(true)}
                        className="h-[30px] ml-[3px] rounded w-[35px] text-[14px] text-[#fff] bg-[#0388CC] flex items-center justify-center cursor-pointer"
                      >
                        <FontAwesomeIcon icon={faLocationDot} />
                      </div>
                    </div>
                  </div>
                  <div className="w-[48%] mt-4 flex items-end">
                    <label className="flex items-center text-[14px] font-[400] cursor-pointer">
                      <input
                        type="checkbox"
                        className="mr-2 rounded"
                        checked={values.hq}
                        onChange={(e) =>
                          setValues({ ...values, hq: e.target.checked })
                        }
                      />
                      Headquarters (shows "HQ &amp; branches" instead of
                      "Authorized distributor")
                    </label>
                  </div>
                </div>
              </div>
              {error ? (
                <p className="text-[14px] ml-6 font-[400] text-[red]">
                  <FontAwesomeIcon icon={faCircleExclamation} /> {error}
                </p>
              ) : null}
              <div className="flex items-center justify-end p-3 rounded-b">
                <div className="flex pb-[10px]">
                  <div
                    onClick={() => setShowModal(false)}
                    className="rounded-full cursor-pointer text-[13px] mr-2 px-6 py-1 text-[#0388CC] bg-[#E9F6FD]"
                  >
                    Cancel
                  </div>
                  <input
                    type="submit"
                    className="cursor-pointer rounded-full text-[13px] px-6 py-1 text-[#fff] bg-[#0388CC]"
                    value="Add to list"
                  />
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
      {showMap && (
        <MapModel
          setShowModal={setShowMap}
          initialLocation={
            hasLocation
              ? { lat: Number(values.lat), lng: Number(values.lng) }
              : { lat: 30, lng: 40 }
          }
          initialZoom={hasLocation ? 5 : 3}
          onPick={({ lat, lng }) =>
            setValues((current) => ({ ...current, lat, lng }))
          }
        />
      )}
      <div className="opacity-25 fixed inset-0 z-40 bg-black"></div>
    </>
  );
}

export default NewCountry;
